import { useMemo, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { describeError } from "../../api/http"
import { keys } from "../../api/keys"
import { attributeWrites, translateWrites } from "../../api/mutations"
import type { TranslateRow, TranslateSource } from "../../api/types"
import type { ConfirmRequest } from "../../hooks/useConfirm"
import {
	CONFIRM_FROM,
	SAVE_CHUNK,
	SHORT_NAME_KEY,
	sameTextKey,
	symbols,
	TRANSLATE_CHUNK,
	trimTranslate,
	UNIT_KEY,
} from "./rules"

/** Долгая операция пачками: показываем счёт и даём прервать. */
export interface Progress {
	kind: "translate" | "save"
	done: number
	total: number
}

/**
 * Черновики справочника и всё, что с ними делают: правка, машинный перевод,
 * сохранение пачками.
 *
 * Здесь же собраны два поведения бэка, ради которых экран вообще устроен так,
 * как устроен:
 *
 * 1. перевод сохраняется во **все** строки раздела с тем же русским текстом
 *    (`TranslateFinder`), поэтому у строки показано «×N», а после сохранения
 *    кэш правится тем же правилом — перечитывать 13 тысяч строк незачем;
 * 2. разные переводы одной русской фразы в одной пачке бэк молча сводит
 *    к первому (`TranslateEditor.cs:137-140`), поэтому такая пачка не
 *    отправляется вовсе.
 */
export function useEdits(options: {
	rows: TranslateRow[]
	catalogId: number | null
	source: TranslateSource
	languageId: number
	languageName: string
	/** Код языка у DeepL (`Languages.DeeplLanguageCode`) — для машинного перевода. */
	deeplCode: string
	/** Контент-менеджер: ему открыт пакетный метод. Переводчику — только построчный. */
	isManager: boolean
	/** Кто правил — уходит в поле `translator` пакетного метода. */
	translator: string
	ask: (request: ConfirmRequest) => Promise<boolean>
}) {
	const { rows, catalogId, source, languageId, languageName, isManager } =
		options
	const client = useQueryClient()

	/** Правки перевода: ключ — id строки выборки. */
	const [edits, setEdits] = useState<Record<number, string>>({})
	/** Правки короткого названия и единицы атрибута: ключ — `sourceId:поле`. */
	const [extras, setExtras] = useState<Record<string, string>>({})

	const [busy, setBusy] = useState(false)
	const [progress, setProgress] = useState<Progress | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [notice, setNotice] = useState<string | null>(null)
	const cancelled = useRef(false)

	const setValue = (row: TranslateRow, value: string) =>
		setEdits(state => ({ ...state, [row.id]: value }))

	const revert = (row: TranslateRow) =>
		setEdits(state => {
			const next = { ...state }
			delete next[row.id]
			return next
		})

	const setExtra = (row: TranslateRow, field: string, value: string) =>
		setExtras(state => ({ ...state, [`${row.sourceId}:${field}`]: value }))

	const discardAll = () => {
		setEdits({})
		setExtras({})
		setNotice(null)
	}

	const valueOf = (row: TranslateRow) => edits[row.id] ?? row.translate ?? ""

	const isDirty = (row: TranslateRow) =>
		edits[row.id] !== undefined &&
		trimTranslate(edits[row.id]) !== (row.translate ?? "")

	/** Сколько строк набора получат тот же перевод — бэк разносит по фразе. */
	const sameText = useMemo(() => {
		const map = new Map<string, number[]>()
		for (const row of rows) {
			const key = sameTextKey(row)
			if (!key) continue
			const list = map.get(key)
			if (list) list.push(row.id)
			else map.set(key, [row.id])
		}
		return map
	}, [rows])

	const dirtyRows = useMemo(
		() =>
			rows.filter(
				row =>
					edits[row.id] !== undefined &&
					trimTranslate(edits[row.id]) !== (row.translate ?? "")
			),
		[rows, edits]
	)

	/** Правки короткого названия и единицы, отличающиеся от того, что в базе. */
	const dirtyExtras = useMemo(() => {
		const stored = new Map<string, string>()
		for (const row of rows) {
			if (!row.additionalInfo) continue
			for (const [field, value] of Object.entries(row.additionalInfo)) {
				stored.set(`${row.sourceId}:${field}`, value ?? "")
			}
		}
		return Object.entries(extras).filter(
			([key, value]) => value !== (stored.get(key) ?? "")
		)
	}, [rows, extras])

	const dirtyCount = dirtyRows.length + dirtyExtras.length

	/** Разные переводы одной русской фразы в одной пачке: бэк возьмёт первый
	 *  и поставит его всем (`TranslateEditor.cs:137-140`). */
	const conflicts = useMemo(() => {
		const byKey = new Map<string, Map<string, string>>()
		for (const row of dirtyRows) {
			const key = sameTextKey(row)
			if (!key) continue
			const variants = byKey.get(key) ?? new Map<string, string>()
			variants.set(trimTranslate(edits[row.id]), row.russian)
			byKey.set(key, variants)
		}
		const found: string[] = []
		for (const variants of byKey.values()) {
			if (variants.size > 1) found.push([...variants.values()][0])
		}
		return found
	}, [dirtyRows, edits])

	/** Правки уходят в базу, а набор в кэше правим сами: перечитывать 13 тысяч
	 *  строк ради собственных же значений незачем. Разнос по одинаковым фразам
	 *  повторяем ровно так, как его делает бэк. */
	const applyToCache = (saved: Map<number, string>) => {
		client.setQueryData<TranslateRow[]>(
			keys.translates(catalogId, source, languageId),
			old => {
				if (!old) return old
				// индексом, а не поиском по массиву: после массового перевода
				// в пачке бывают тысячи строк, а в наборе — десятки тысяч
				const byId = new Map(old.map(row => [row.id, row]))
				const spread = new Map<string, string>()
				for (const [id, value] of saved) {
					const row = byId.get(id)
					const key = row && sameTextKey(row)
					if (key) spread.set(key, value)
				}
				return old.map(row => {
					const direct = saved.get(row.id)
					const key = sameTextKey(row)
					const value = direct ?? (key ? spread.get(key) : undefined)
					return value === undefined ? row : { ...row, translate: value }
				})
			}
		)
		// в других наборах те же фразы могли поменяться — пусть перечитаются,
		// когда до них дойдут; сейчас никого не дёргаем
		void client.invalidateQueries({
			queryKey: keys.translates(),
			refetchType: "none",
		})
	}

	/** Сколько строк набора реально задело сохранение: правленые плюс те, куда
	 *  бэк разнёс перевод по совпадающей русской фразе. */
	const spreadOf = (saved: TranslateRow[]) => {
		const affected = new Set<number>()
		for (const row of saved) {
			const key = sameTextKey(row)
			const twins = key ? sameText.get(key) : undefined
			if (twins) for (const id of twins) affected.add(id)
			else affected.add(row.id)
		}
		return affected.size
	}

	/** Только то, что действительно записалось: пачка могла оборваться. */
	const applyExtrasToCache = (savedExtras: string[]) => {
		const done = new Set(savedExtras)
		client.setQueryData<TranslateRow[]>(
			keys.translates(catalogId, source, languageId),
			old =>
				old?.map(row => {
					const shortKey = `${row.sourceId}:${SHORT_NAME_KEY}`
					const unitKey = `${row.sourceId}:${UNIT_KEY}`
					const short = done.has(shortKey) ? extras[shortKey] : undefined
					const unit = done.has(unitKey) ? extras[unitKey] : undefined
					if (short === undefined && unit === undefined) return row
					return {
						...row,
						additionalInfo: {
							...(row.additionalInfo ?? {}),
							...(short !== undefined ? { [SHORT_NAME_KEY]: short } : {}),
							...(unit !== undefined ? { [UNIT_KEY]: unit } : {}),
						},
					}
				})
		)
	}

	const save = async () => {
		if (busy || dirtyCount === 0) return
		if (conflicts.length > 0) {
			setError(
				"Одной русской фразе заданы разные переводы — бэк сохранит только " +
					`один и подставит его во все строки. Приведите к одному: «${conflicts[0]}»`
			)
			return
		}

		setBusy(true)
		setError(null)
		setNotice(null)
		cancelled.current = false
		setProgress({ kind: "save", done: 0, total: dirtyCount })

		// что реально доехало до базы: пачка могла оборваться на середине
		const saved = new Map<number, string>()
		const savedRows: TranslateRow[] = []
		const savedExtras: string[] = []

		const remember = (row: TranslateRow) => {
			saved.set(row.id, trimTranslate(edits[row.id]))
			savedRows.push(row)
		}

		const report = () =>
			setProgress({
				kind: "save",
				done: savedRows.length + savedExtras.length,
				total: dirtyCount,
			})

		try {
			for (let at = 0; at < dirtyRows.length; at += SAVE_CHUNK) {
				if (cancelled.current) break
				const chunk = dirtyRows.slice(at, at + SAVE_CHUNK)
				if (isManager) {
					await translateWrites.saveBatch(
						languageId,
						options.translator,
						chunk.map(row => ({
							translateId: row.translateId,
							sourceId: row.sourceId,
							sourceTable: row.source,
							translate: trimTranslate(edits[row.id]),
						}))
					)
					for (const row of chunk) remember(row)
				} else {
					// роль Translator: пакетный метод ей закрыт, идём построчно
					for (const row of chunk) {
						if (cancelled.current) break
						await translateWrites.updateOne(
							languageId,
							row,
							trimTranslate(edits[row.id])
						)
						remember(row)
						report()
					}
				}
				report()
			}

			// короткое название и единица атрибута лежат вне Translates
			for (const [key, value] of dirtyExtras) {
				if (cancelled.current) break
				const [sourceId, field] = key.split(":")
				const attributeId = Number(sourceId)
				if (field === SHORT_NAME_KEY) {
					await attributeWrites.shortName(attributeId, value, languageId)
				} else {
					await attributeWrites.unit(attributeId, value, languageId)
				}
				savedExtras.push(key)
				report()
			}
		} catch (thrown) {
			setError(describeError(thrown))
		} finally {
			if (savedExtras.length > 0) applyExtrasToCache(savedExtras)
			if (saved.size > 0) applyToCache(saved)

			// в полях оставляем только то, что не доехало — остальное уже в базе
			if (savedRows.length > 0) {
				setEdits(state => {
					const next = { ...state }
					for (const row of savedRows) delete next[row.id]
					return next
				})
			}
			if (savedExtras.length > 0) {
				setExtras(state => {
					const next = { ...state }
					for (const key of savedExtras) delete next[key]
					return next
				})
			}

			const done = savedRows.length + savedExtras.length
			if (done > 0) {
				const spread = spreadOf(savedRows)
				setNotice(
					`Сохранено строк: ${done}` +
						(spread > savedRows.length
							? ` · с учётом одинаковых фраз обновлено ${spread}`
							: "") +
						(done < dirtyCount ? ` · осталось ${dirtyCount - done}` : "")
				)
			}
			setProgress(null)
			setBusy(false)
		}
	}

	/**
	 * Машинный перевод. Одинаковые русские фразы шлём один раз — в наборе их
	 * много (у строки видно «×N»), и каждая лишняя стоит символов в квоте DeepL.
	 * Ходим пачками: виден прогресс, работает отмена, и то, что уже перевелось,
	 * остаётся в полях, даже если дальше пришла ошибка.
	 */
	const machineTranslate = async (targets: TranslateRow[]) => {
		if (busy || targets.length === 0) return

		const values = [
			...new Set(
				targets.map(row => row.russian).filter(text => !!text && text.trim())
			),
		]
		if (values.length === 0) {
			setError("В выбранных строках нет русского текста")
			return
		}

		if (values.length >= CONFIRM_FROM) {
			const chars = values.reduce((sum, value) => sum + symbols(value), 0)
			const yes = await options.ask({
				title: `Перевести машинно на ${languageName}?`,
				text: `Строк: ${targets.length}, разных фраз: ${values.length}, символов: ${chars.toLocaleString("ru")}. Символы спишутся с квоты DeepL. Результат встанет в поля черновиком — в базу ничего не уйдёт, пока не нажмёте «Сохранить».`,
				confirm: "Перевести",
			})
			if (!yes) return
		}

		setBusy(true)
		setError(null)
		setNotice(null)
		cancelled.current = false
		setProgress({ kind: "translate", done: 0, total: values.length })

		const byOriginal = new Map<string, string>()
		let failure: unknown = null

		try {
			for (let at = 0; at < values.length; at += TRANSLATE_CHUNK) {
				if (cancelled.current) break
				const chunk = values.slice(at, at + TRANSLATE_CHUNK)
				const answer = await translateWrites.byMachine(options.deeplCode, chunk)
				const before = byOriginal.size
				for (const item of answer) {
					if (item.original) byOriginal.set(item.original, item.translated)
				}
				// подстраховка: если переводчик вернул оригиналы в изменённом виде,
				// но столько же и в том же порядке — сопоставляем по позиции
				if (byOriginal.size === before && answer.length === chunk.length) {
					chunk.forEach((value, index) =>
						byOriginal.set(value, answer[index].translated)
					)
				}
				setProgress({
					kind: "translate",
					done: Math.min(at + chunk.length, values.length),
					total: values.length,
				})
			}
		} catch (thrown) {
			failure = thrown
		} finally {
			const next: Record<number, string> = {}
			let filled = 0
			for (const row of targets) {
				const translated = byOriginal.get(row.russian)
				if (translated === undefined || translated === null) continue
				next[row.id] = translated
				filled++
			}
			if (filled > 0) setEdits(state => ({ ...state, ...next }))

			if (failure) setError(describeError(failure))
			setNotice(
				filled === 0
					? failure
						? null
						: "Переводчик ничего не вернул"
					: `Переведено машинно строк: ${filled}` +
							(cancelled.current ? " · остановлено" : "") +
							". Проверьте и сохраните — в базу ещё ничего не ушло"
			)
			setProgress(null)
			setBusy(false)
		}
	}

	return {
		edits,
		extras,
		setValue,
		revert,
		setExtra,
		discardAll,
		valueOf,
		isDirty,
		sameText,
		dirtyRows,
		dirtyCount,
		conflicts,

		busy,
		progress,
		cancelled,
		cancel: () => {
			cancelled.current = true
			setProgress(state => (state ? { ...state } : state))
		},

		error,
		notice,
		setError,
		setNotice,

		save,
		machineTranslate,
	}
}
